import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Hidden from '@material-ui/core/Hidden';
import Grid from '@material-ui/core/Grid';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import Avatar from '@material-ui/core/Avatar';
import Typography from '@material-ui/core/Typography';
import Rating from 'components/Rating';
import ButtonBase from '@material-ui/core/ButtonBase';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { makeSelectAllowSignIn } from 'containers/Main/selectors';
import format from 'date-fns/format';
import useDateLocale from 'components/useDateLocale';
import { constructUserReviewsData, getIcon } from './utils';
import WriteReview from './WriteReview';

const useStyles = makeStyles(theme => ({
  root: {
    border: '1px solid #D8D8D8',
    padding: 17,
    marginTop: 33,
    marginBottom: 20,
  },
  wrapper: {
    marginBottom: 14,
    [theme.breakpoints.up('sm')]: {
      marginBottom: 24,
    },
  },
  avatar: {
    width: 48,
    height: 48,
    marginRight: 13,
    [theme.breakpoints.up('sm')]: {
      marginRight: 23,
    },
  },
  starAndDateWrapper: {
    display: 'flex',
    marginBottom: 10,
    alignItems: 'center',
  },
  starIcon: {
    height: 12,
  },
  date: {
    color: theme.palette.text.disabled,
    [theme.breakpoints.up('sm')]: {
      marginLeft: 11,
    },
  },
  typeIcon: {
    marginLeft: 10,
  },
  writeReviewWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    '& > button': {
      color: '#01619B',
      fontSize: 14,
      marginRight: 15,
      height: 39,
    },
  },
  linkBtnWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    marginTop: 24,
    '& button': {
      color: '#01619B',
      fontSize: 14,
    },
  },
  body: {
    display: '-webkit-box',
    '-webkit-line-clamp': '5',
    '-webkit-box-orient': 'vertical',
    overflow: props => (props.isIE ? 'visible' : 'hidden'),
    textOverflow: 'ellipsis',
  },
  noReviewWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  noReviewLabel: {
    fontSize: '1.0625rem',
    fontFamily: 'MadaBold',
    verticalAlign: 'middle',
    color: theme.palette.text.primary,
    [theme.breakpoints.down('xs')]: {
      lineHeight: 1,
    },
  },
}));
const avatarColorSet = ['#66C2B6', '#64BDDF', '#B270C5'];

function TopReview(props) {
  const {
    data = [],
    items,
    id,
    rate,
    review,
    sendReview,
    isAdmin,
    cmUserReviews,
  } = props;
  const classes = useStyles();
  const colorIndex = useRef(Math.floor(Math.random() * 3));
  const [userReviewsSiteCopy] = useSiteCopySelector(['user-reviews']);
  const allowSignIn = useSelector(makeSelectAllowSignIn());
  const formattedData = [...constructUserReviewsData(data), ...cmUserReviews];
  const finalData = formattedData.sort(
    (a, b) => b.rating - a.rating || new Date(b.date) - new Date(a.date),
  );
  // if (_isEmpty(finalData)) return null;
  const smoothScroll = idEl => {
    const $anchor = document.getElementById(idEl);
    if ($anchor) {
      const offsetTop =
        $anchor.getBoundingClientRect().top + window.pageYOffset;
      window.scroll({
        top: offsetTop - 150,
        behavior: 'smooth',
      });
    }
  };
  const dateFormatFilter = useDateLocale();
  const topData = finalData[0];
  const userReviewsProps = {
    id,
    isAdmin,
    data: items,
    rate,
    review,
    sendReview,
    siteCopy: userReviewsSiteCopy,
  };
  const hasReview = !_isEmpty(_get(topData, 'body'));
  const hasPerson = !_isEmpty(_get(topData, 'person'));

  const {
    body = '',
    date = '',
    person = '',
    rating = '',
    person_img = '',
    type = '',
  } = topData || {};

  const formattedDate = date ? !Number.isNaN(Number(date)) : '';
  const [firstName = '', lastName = ''] = person.split(' ');

  const personName = `${firstName} ${lastName &&
    lastName.charAt(0).toUpperCase()}`;

  const shouldDisplayReview = hasPerson && hasReview;

  if (!shouldDisplayReview && !allowSignIn) return null;

  return (
    <Grid container className={classes.root}>
      <Grid item xs={12}>
        <Grid container spacing={1} justifyContent="space-between">
          {!shouldDisplayReview && allowSignIn && (
            <Grid item xs={6} sm className={classes.noReviewWrapper}>
              <Typography className={classes.noReviewLabel}>
                {_get(userReviewsSiteCopy, 'pageCopy.emptyReviewsMessage', '')}
              </Typography>
            </Grid>
          )}
          {shouldDisplayReview && (
            <Grid item xs={6} sm>
              <Grid container className={classes.wrapper}>
                <Grid item>
                  <Avatar
                    src={person_img}
                    className={classes.avatar}
                    style={{
                      backgroundColor: avatarColorSet[colorIndex.current],
                    }}
                    alt={person}
                  >
                    {!person_img && person.charAt(0)}
                  </Avatar>
                </Grid>

                <Grid item xs>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textPrimary">
                        {personName}
                      </Typography>
                      <img
                        src={getIcon(type)}
                        alt={type}
                        className={classes.typeIcon}
                      />
                    </Grid>
                    <Grid item xs={12} className={classes.starAndDateWrapper}>
                      <Hidden smUp>
                        <Grid container>
                          <Grid item xs={12} sm>
                            <Rating name="user-rating" value={rating} />
                          </Grid>
                          <Grid item xs={12} sm>
                            <Typography
                              variant="overline"
                              color="textPrimary"
                              className={classes.date}
                            >
                              {formattedDate
                                ? format(date, 'MMM dd, yyyy', {
                                    locale: dateFormatFilter,
                                  })
                                : date}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Hidden>
                      <Hidden xsDown>
                        <Rating name="user-rating" value={rating} />
                        <Typography
                          variant="overline"
                          color="textPrimary"
                          className={classes.date}
                        >
                          {formattedDate
                            ? format(date, 'MMM dd, yyyy', {
                                locale: dateFormatFilter,
                              })
                            : date}
                        </Typography>
                      </Hidden>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          {(allowSignIn || shouldDisplayReview) && (
            <Grid item className={classes.writeReviewWrapper}>
              {shouldDisplayReview && (
                <Hidden xsDown>
                  <ButtonBase
                    onClick={() => smoothScroll('user-reviews-panel')}
                  >
                    {_get(userReviewsSiteCopy, 'pageCopy.moreReviewsLabel', '')}
                  </ButtonBase>
                </Hidden>
              )}
              {allowSignIn && <WriteReview {...userReviewsProps} />}
            </Grid>
          )}
        </Grid>
      </Grid>
      {shouldDisplayReview && (
        <Grid item xs={12}>
          <Typography
            variant="overline"
            color="textPrimary"
            className={classes.body}
          >
            {body}
          </Typography>
        </Grid>
      )}
      {shouldDisplayReview && (
        <Hidden smUp>
          <Grid item xs={12} className={classes.linkBtnWrapper}>
            <ButtonBase onClick={() => smoothScroll('user-reviews-panel')}>
              {_get(userReviewsSiteCopy, 'pageCopy.readMoreReviewsLabel', '')}
            </ButtonBase>
          </Grid>
        </Hidden>
      )}
    </Grid>
  );
}

TopReview.propTypes = {
  data: PropTypes.object,
  items: PropTypes.array,
  isAdmin: PropTypes.bool,
  rate: PropTypes.func,
  review: PropTypes.object,
  sendReview: PropTypes.func,
  id: PropTypes.string,
  cmUserReviews: PropTypes.array,
  type: PropTypes.string,
};

export default TopReview;
